import { useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Clock3 } from "lucide-react";
import api from "../lib/api";
import { useSiteContent } from "../context/ContentContext";

const OUNCES_PER_KILOGRAM = 32.1507466;
const GRAMS_PER_TROY_OUNCE = 31.1034768;

function formatUsd(value, maximumFractionDigits = 2) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(value);
}

function formatTime(isoString) {
  if (!isoString) {
    return "Live";
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return "Live";
  }

  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
}

function buildItems(prices) {
  const goldOz = prices?.gold?.price ?? 2655.3;
  const silverOz = prices?.silver?.price ?? 30.85;

  return [
    {
      key: "gold-oz",
      label: "Gold / oz",
      value: goldOz,
      change: prices?.gold?.change ?? 0,
      changePercent: prices?.gold?.change_percent ?? 0,
    },
    {
      key: "gold-gram",
      label: "Gold / g",
      value: goldOz / GRAMS_PER_TROY_OUNCE,
      change: (prices?.gold?.change ?? 0) / GRAMS_PER_TROY_OUNCE,
      changePercent: prices?.gold?.change_percent ?? 0,
    },
    {
      key: "gold-kg",
      label: "Gold / kg",
      value: goldOz * OUNCES_PER_KILOGRAM,
      change: (prices?.gold?.change ?? 0) * OUNCES_PER_KILOGRAM,
      changePercent: prices?.gold?.change_percent ?? 0,
    },
    {
      key: "silver-oz",
      label: "Silver / oz",
      value: silverOz,
      change: prices?.silver?.change ?? 0,
      changePercent: prices?.silver?.change_percent ?? 0,
    },
    {
      key: "silver-gram",
      label: "Silver / g",
      value: silverOz / GRAMS_PER_TROY_OUNCE,
      change: (prices?.silver?.change ?? 0) / GRAMS_PER_TROY_OUNCE,
      changePercent: prices?.silver?.change_percent ?? 0,
    },
    {
      key: "silver-kg",
      label: "Silver / kg",
      value: silverOz * OUNCES_PER_KILOGRAM,
      change: (prices?.silver?.change ?? 0) * OUNCES_PER_KILOGRAM,
      changePercent: prices?.silver?.change_percent ?? 0,
    },
  ];
}

function TickerItem({ item }) {
  const positive = item.change >= 0;
  const TrendIcon = positive ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="price-ticker-pill">
      <div className="text-[11px] uppercase tracking-[0.2em] theme-text-accent font-semibold">
        {item.label}
      </div>
      <div className="font-display text-lg sm:text-xl font-bold theme-text-primary">
        {formatUsd(item.value)}
      </div>
      <div className={`inline-flex items-center gap-1 text-xs font-semibold ${positive ? "text-emerald-600" : "text-rose-600"}`}>
        <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" />
        <span>
          {positive ? "+" : ""}
          {formatUsd(item.change, 3)} ({item.changePercent.toFixed(2)}%)
        </span>
      </div>
    </div>
  );
}

export default function PriceTicker() {
  const { content } = useSiteContent();
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchPrices = async () => {
      try {
        const { data } = await api.get("/market/prices");
        if (mounted) {
          setPrices(data);
        }
      } catch (error) {
        // Keep the fallback ticker visible even if the request fails.
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPrices();
    const intervalId = window.setInterval(fetchPrices, 60000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const items = useMemo(() => buildItems(prices), [prices]);
  const tickerItems = useMemo(() => [...items, ...items], [items]);
  const updatedAt = formatTime(prices?.fetched_at);
  const tickerCopy = content?.ticker || {};
  const heading = tickerCopy.heading || "Live Metals Prices";
  const updating = tickerCopy.updating || "Updating...";
  const updatedPrefix = tickerCopy.updated_prefix || "Updated";
  const utcSuffix = tickerCopy.utc_suffix || "UTC";
  const updatedLabel = loading
    ? updating
    : `${updatedPrefix} ${updatedAt}${utcSuffix ? ` ${utcSuffix}` : ""}`;

  return (
    <section className="theme-bg-primary border-t theme-border" data-testid="price-ticker-section" aria-label="Live precious metals prices">
      <div className="container-x py-4 sm:py-5">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="eyebrow mb-0">{heading}</div>
            <div className="inline-flex items-center gap-2 rounded-full theme-bg-secondary px-3 py-1 text-xs font-semibold theme-text-secondary">
              <Clock3 className="h-3.5 w-3.5 theme-text-accent" aria-hidden="true" />
              <span>{updatedLabel}</span>
            </div>
          </div>
        </div>

        <div className="price-ticker-shell mt-4" aria-live="polite">
          <div className="price-ticker-track">
            {tickerItems.map((item, index) => (
              <TickerItem key={`${item.key}-${index}`} item={item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
